import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Leaf, ArrowLeft, ArrowRight, Save, Loader2, CheckCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_HORIZONTAL = "https://customer-assets.emergentagent.com/job_4aa3e30f-645a-43e8-83ed-b83576a14c9f/artifacts/p43xkrnl_SusTek%20Colored%20Logo%20Name%20-%20Horizontal.svg";

// Country list for dropdown
const countries = [
  "United States", "United Kingdom", "Germany", "France", "China", "Japan", 
  "India", "Canada", "Australia", "Brazil", "Mexico", "Singapore", "Netherlands",
  "Switzerland", "South Korea", "Italy", "Spain", "Sweden", "Norway", "Denmark"
].sort();

const countryCodes = [
  { code: "+1", country: "US/CA" },
  { code: "+44", country: "UK" },
  { code: "+49", country: "DE" },
  { code: "+33", country: "FR" },
  { code: "+86", country: "CN" },
  { code: "+81", country: "JP" },
  { code: "+91", country: "IN" },
  { code: "+61", country: "AU" },
  { code: "+65", country: "SG" },
];

const certificationOptions = [
  "ISO 14001", "ISO 45001", "ISO 50001", "B Corp", "EcoVadis",
  "FSC / PEFC", "RSPO", "SBTi Validated", "None", "Other"
];

const regulatoryOptions = [
  "GDPR", "CCPA", "PDPA (Singapore)", "HIPAA", "ISO 27001", "SOC 2", "None", "Other"
];

const securityCertOptions = [
  "ISO 27001", "SOC 2 Type II", "NIST-aligned", "None", "Other"
];

const securityControlOptions = [
  "Multi-factor authentication (MFA)",
  "Role-based access control (RBAC)",
  "Encryption at rest",
  "Encryption in transit",
  "Annual penetration testing",
  "Incident response plan",
  "None"
];

const ESGQuestionnaire = () => {
  const navigate = useNavigate();
  const [supplier, setSupplier] = useState(null);
  const [currentSection, setCurrentSection] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [questionnaireData, setQuestionnaireData] = useState({
    supplier_profile: {
      supplier_name: "",
      headquarters_address: "",
      country: "",
      country_code: "",
      phone_number: "",
      sustainability_lead: "",
      job_title: "",
      email: ""
    },
    governance_strategy: {
      esg_strategy_status: "",
      certifications: [],
      certification_expiry_dates: {},
      data_protection_policy: "",
      regulatory_compliance: [],
      data_breach_history: "",
      data_breach_description: "",
      security_certifications: [],
      security_controls: [],
      cybersecurity_oversight: null,
      code_of_conduct: "",
      anti_corruption_policy: null,
      whistleblower_mechanism: "",
      esg_fines: "",
      esg_fines_description: ""
    },
    climate_emissions: {
      scope1_2_emissions: "",
      reporting_year: "",
      sbti_status: "",
      target_year: ""
    },
    water_management: {
      total_water_withdrawal: "",
      water_stress_level: "",
      water_mitigation_plan: "",
      water_plan_description: ""
    },
    waste_circularity: {
      waste_diversion_rate: "",
      circular_innovation: "",
      circular_description: ""
    },
    social_responsibility: {
      total_employees: "",
      permanent_employee_percent: "",
      women_management_percent: "",
      diversity_policy: "",
      child_labor_policy: null,
      forced_labor_policy: null,
      human_rights_assessment: "",
      collective_bargaining: "",
      health_safety_system: "",
      ltifr: "",
      safety_training_percent: "",
      community_investment: "",
      community_profit_percent: ""
    },
    advanced_metrics: {
      revenue_usd: "",
      production_volume: "",
      employees_count: ""
    }
  });

  const sections = [
    { id: 0, name: "Supplier Profile", key: "supplier_profile" },
    { id: 1, name: "Governance & Strategy", key: "governance_strategy" },
    { id: 2, name: "Climate & Emissions", key: "climate_emissions" },
    { id: 3, name: "Water Management", key: "water_management" },
    { id: 4, name: "Waste & Circularity", key: "waste_circularity" },
    { id: 5, name: "Social Responsibility", key: "social_responsibility" },
    { id: 6, name: "Advanced Metrics", key: "advanced_metrics" }
  ];

  useEffect(() => {
    const supplierData = localStorage.getItem("supplier");
    if (!supplierData) {
      toast.error("Please login to access the questionnaire");
      navigate("/auth");
      return;
    }
    
    const parsedSupplier = JSON.parse(supplierData);
    setSupplier(parsedSupplier);
    loadQuestionnaire(parsedSupplier.id);
  }, [navigate]);

  const loadQuestionnaire = async (supplierId) => {
    try {
      const response = await axios.get(`${API}/questionnaire/${supplierId}`);
      if (response.data) {
        const data = response.data;
        setQuestionnaireData(prev => ({
          supplier_profile: data.supplier_profile || prev.supplier_profile,
          governance_strategy: data.governance_strategy || prev.governance_strategy,
          climate_emissions: data.climate_emissions || prev.climate_emissions,
          water_management: data.water_management || prev.water_management,
          waste_circularity: data.waste_circularity || prev.waste_circularity,
          social_responsibility: data.social_responsibility || prev.social_responsibility,
          advanced_metrics: data.advanced_metrics || prev.advanced_metrics
        }));
        setCurrentSection(data.current_section || 0);
      }
    } catch (error) {
      console.error("Error loading questionnaire:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const saveProgress = async () => {
    if (!supplier) return;
    
    setIsSaving(true);
    try {
      await axios.post(`${API}/questionnaire/${supplier.id}`, {
        ...questionnaireData,
        current_section: currentSection
      });
      toast.success("Progress saved");
    } catch (error) {
      toast.error("Failed to save progress");
    } finally {
      setIsSaving(false);
    }
  };

  const handleNext = async () => {
    await saveProgress();
    if (currentSection < sections.length - 1) {
      setCurrentSection(currentSection + 1);
      window.scrollTo(0, 0);
    }
  };

  const handlePrevious = () => {
    if (currentSection > 0) {
      setCurrentSection(currentSection - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/questionnaire/${supplier.id}`, {
        ...questionnaireData,
        current_section: sections.length - 1
      });
      await axios.post(`${API}/questionnaire/${supplier.id}/submit`);
      toast.success("Questionnaire submitted successfully!");
      navigate("/submitted");
    } catch (error) {
      toast.error("Failed to submit questionnaire");
    } finally {
      setIsSubmitting(false);
    }
  };

  const updateSection = (section, field, value) => {
    setQuestionnaireData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const toggleArrayItem = (section, field, item) => {
    setQuestionnaireData(prev => {
      const currentArray = prev[section][field] || [];
      const newArray = currentArray.includes(item)
        ? currentArray.filter(i => i !== item)
        : [...currentArray, item];
      return {
        ...prev,
        [section]: {
          ...prev[section],
          [field]: newArray
        }
      };
    });
  };

  const progress = ((currentSection + 1) / sections.length) * 100;

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-cream to-white flex items-center justify-center">
        <Loader2 className="w-12 h-12 text-forest animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-cream to-white">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50" data-testid="questionnaire-header">
        <div className="container-main py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="questionnaire-logo">
            <img src={LOGO_HORIZONTAL} alt="SusTek" className="h-10" />
          </Link>
          <div className="flex items-center gap-4">
            <span className="text-sm text-muted-foreground hidden sm:block">
              Logged in as: <span className="font-medium text-foreground">{supplier?.company_name}</span>
            </span>
            <Button 
              variant="outline" 
              size="sm"
              onClick={saveProgress}
              disabled={isSaving}
              data-testid="questionnaire-save-btn"
            >
              {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              <span className="ml-2 hidden sm:inline">Save</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Progress Bar */}
      <div className="sticky top-[73px] z-40 bg-white border-b border-border py-4">
        <div className="container-main">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-foreground">
              Section {currentSection + 1} of {sections.length}: {sections[currentSection].name}
            </span>
            <span className="text-sm text-muted-foreground">{Math.round(progress)}% Complete</span>
          </div>
          <Progress value={progress} className="h-2" />
          <div className="flex gap-1 mt-2 overflow-x-auto pb-2">
            {sections.map((section, index) => (
              <button
                key={section.id}
                onClick={() => setCurrentSection(index)}
                className={`px-3 py-1 text-xs rounded-full whitespace-nowrap transition-colors ${
                  index === currentSection
                    ? "bg-forest text-white"
                    : index < currentSection
                    ? "bg-sage/20 text-sage"
                    : "bg-muted text-muted-foreground"
                }`}
                data-testid={`section-nav-${index}`}
              >
                {section.name}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Form Content */}
      <main className="container-main py-8">
        <Card className="max-w-3xl mx-auto border-border/50 shadow-sm" data-testid="questionnaire-form">
          <CardHeader>
            <CardTitle className="font-heading text-2xl text-foreground">
              {sections[currentSection].name}
            </CardTitle>
            <CardDescription>
              {currentSection === 0 && "Please provide your company identification details"}
              {currentSection === 1 && "Information about your sustainability strategy and governance"}
              {currentSection === 2 && "Details about your carbon emissions and climate commitments"}
              {currentSection === 3 && "Information about your water usage and management"}
              {currentSection === 4 && "Details about waste management and circular economy initiatives"}
              {currentSection === 5 && "Information about workforce, health & safety, and community impact"}
              {currentSection === 6 && "Optional advanced metrics for enhanced ESG scoring"}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Section 0: Supplier Profile */}
            {currentSection === 0 && (
              <div className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="supplier_name">Supplier Name *</Label>
                    <Input
                      id="supplier_name"
                      placeholder="Legal registered name"
                      value={questionnaireData.supplier_profile.supplier_name}
                      onChange={(e) => updateSection("supplier_profile", "supplier_name", e.target.value)}
                      data-testid="input-supplier-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="contact@company.com"
                      value={questionnaireData.supplier_profile.email}
                      onChange={(e) => updateSection("supplier_profile", "email", e.target.value)}
                      data-testid="input-email"
                    />
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="headquarters_address">Corporate Headquarters Address *</Label>
                  <Textarea
                    id="headquarters_address"
                    placeholder="Full mailing address"
                    value={questionnaireData.supplier_profile.headquarters_address}
                    onChange={(e) => updateSection("supplier_profile", "headquarters_address", e.target.value)}
                    data-testid="input-address"
                  />
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="country">Country *</Label>
                    <Select
                      value={questionnaireData.supplier_profile.country}
                      onValueChange={(value) => updateSection("supplier_profile", "country", value)}
                    >
                      <SelectTrigger data-testid="select-country">
                        <SelectValue placeholder="Select country" />
                      </SelectTrigger>
                      <SelectContent>
                        {countries.map(country => (
                          <SelectItem key={country} value={country}>{country}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>Phone Number *</Label>
                    <div className="flex gap-2">
                      <Select
                        value={questionnaireData.supplier_profile.country_code}
                        onValueChange={(value) => updateSection("supplier_profile", "country_code", value)}
                      >
                        <SelectTrigger className="w-28" data-testid="select-country-code">
                          <SelectValue placeholder="Code" />
                        </SelectTrigger>
                        <SelectContent>
                          {countryCodes.map(c => (
                            <SelectItem key={c.code} value={c.code}>{c.code} {c.country}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Input
                        placeholder="Phone number"
                        value={questionnaireData.supplier_profile.phone_number}
                        onChange={(e) => updateSection("supplier_profile", "phone_number", e.target.value)}
                        data-testid="input-phone"
                      />
                    </div>
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="sustainability_lead">Sustainability Lead / Point of Contact *</Label>
                    <Input
                      id="sustainability_lead"
                      placeholder="Full name"
                      value={questionnaireData.supplier_profile.sustainability_lead}
                      onChange={(e) => updateSection("supplier_profile", "sustainability_lead", e.target.value)}
                      data-testid="input-sustainability-lead"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="job_title">Job Title *</Label>
                    <Input
                      id="job_title"
                      placeholder="Role of ESG lead"
                      value={questionnaireData.supplier_profile.job_title}
                      onChange={(e) => updateSection("supplier_profile", "job_title", e.target.value)}
                      data-testid="input-job-title"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Section 1: Governance & Strategy */}
            {currentSection === 1 && (
              <div className="space-y-6">
                <div className="space-y-3">
                  <Label>Do you have a documented sustainability / ESG strategy? *</Label>
                  <RadioGroup
                    value={questionnaireData.governance_strategy.esg_strategy_status}
                    onValueChange={(value) => updateSection("governance_strategy", "esg_strategy_status", value)}
                    data-testid="radio-esg-strategy"
                  >
                    {["Yes - Published externally", "Yes - Internal document only", "No - In development", "No - Not available"].map(option => (
                      <div key={option} className="flex items-center space-x-2">
                        <RadioGroupItem value={option} id={`strategy-${option}`} />
                        <Label htmlFor={`strategy-${option}`} className="font-normal">{option}</Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>

                <div className="space-y-3">
                  <Label>Which sustainability certifications do you currently hold?</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {certificationOptions.map(cert => (
                      <div key={cert} className="flex items-center space-x-2">
                        <Checkbox
                          id={`cert-${cert}`}
                          checked={questionnaireData.governance_strategy.certifications?.includes(cert)}
                          onCheckedChange={() => toggleArrayItem("governance_strategy", "certifications", cert)}
                          data-testid={`checkbox-cert-${cert.toLowerCase().replace(/\s/g, '-')}`}
                        />
                        <Label htmlFor={`cert-${cert}`} className="font-normal text-sm">{cert}</Label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-3">
                  <Label>Do you have a formal data protection and privacy policy? *</Label>
                  <RadioGroup
                    value={questionnaireData.governance_strategy.data_protection_policy}
                    onValueChange={(value) => updateSection("governance_strategy", "data_protection_policy", value)}
                    data-testid="radio-data-protection"
                  >
                    {["Yes – Publicly available", "Yes – Internal policy", "In development", "No"].map(option => (
                      <div key={option} className="flex items-center space-x-2">
                        <RadioGroupItem value={option} id={`data-${option}`} />
                        <Label htmlFor={`data-${option}`} className="font-normal">{option}</Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>

                <div className="space-y-3">
                  <Label>Which data protection regulations do you comply with?</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {regulatoryOptions.map(reg => (
                      <div key={reg} className="flex items-center space-x-2">
                        <Checkbox
                          id={`reg-${reg}`}
                          checked={questionnaireData.governance_strategy.regulatory_compliance?.includes(reg)}
                          onCheckedChange={() => toggleArrayItem("governance_strategy", "regulatory_compliance", reg)}
                          data-testid={`checkbox-reg-${reg.toLowerCase().replace(/\s/g, '-')}`}
                        />
                        <Label htmlFor={`reg-${reg}`} className="font-normal text-sm">{reg}</Label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="space-y-3">
                  <Label>Have you experienced any material data breaches in the past 3 years?</Label>
                  <RadioGroup
                    value={questionnaireData.governance_strategy.data_breach_history}
                    onValueChange={(value) => updateSection("governance_strategy", "data_breach_history", value)}
                    data-testid="radio-data-breach"
                  >
                    {["No", "Yes - Resolved", "Yes - Ongoing remediation"].map(option => (
                      <div key={option} className="flex items-center space-x-2">
                        <RadioGroupItem value={option} id={`breach-${option}`} />
                        <Label htmlFor={`breach-${option}`} className="font-normal">{option}</Label>
                      </div>
                    ))}
                  </RadioGroup>
                  {questionnaireData.governance_strategy.data_breach_history?.includes("Yes") && (
                    <Textarea
                      placeholder="Describe incident and corrective action"
                      value={questionnaireData.governance_strategy.data_breach_description}
                      onChange={(e) => updateSection("governance_strategy", "data_breach_description", e.target.value)}
                      className="mt-2"
                      data-testid="textarea-breach-description"
                    />
                  )}
                </div>

                <div className="space-y-3">
                  <Label>Do you maintain a formal Code of Business Conduct and Ethics?</Label>
                  <RadioGroup
                    value={questionnaireData.governance_strategy.code_of_conduct}
                    onValueChange={(value) => updateSection("governance_strategy", "code_of_conduct", value)}
                    data-testid="radio-code-conduct"
                  >
                    {["Yes - Public", "Yes - Internal", "In development", "No"].map(option => (
                      <div key={option} className="flex items-center space-x-2">
                        <RadioGroupItem value={option} id={`conduct-${option}`} />
                        <Label htmlFor={`conduct-${option}`} className="font-normal">{option}</Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>

                <div className="space-y-3">
                  <Label>Do you have a documented anti-bribery and anti-corruption policy?</Label>
                  <RadioGroup
                    value={questionnaireData.governance_strategy.anti_corruption_policy === true ? "Yes" : questionnaireData.governance_strategy.anti_corruption_policy === false ? "No" : ""}
                    onValueChange={(value) => updateSection("governance_strategy", "anti_corruption_policy", value === "Yes")}
                    data-testid="radio-anti-corruption"
                  >
                    {["Yes", "No"].map(option => (
                      <div key={option} className="flex items-center space-x-2">
                        <RadioGroupItem value={option} id={`corruption-${option}`} />
                        <Label htmlFor={`corruption-${option}`} className="font-normal">{option}</Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>
              </div>
            )}

            {/* Section 2: Climate & Emissions */}
            {currentSection === 2 && (
              <div className="space-y-6">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="emissions">Total Scope 1 + Scope 2 emissions (tCO₂e) *</Label>
                    <Input
                      id="emissions"
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="e.g., 15000.00"
                      value={questionnaireData.climate_emissions.scope1_2_emissions}
                      onChange={(e) => updateSection("climate_emissions", "scope1_2_emissions", e.target.value)}
                      data-testid="input-emissions"
                    />
                    <p className="text-xs text-muted-foreground">For most recent reporting year</p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="reporting_year">Reporting Year *</Label>
                    <Select
                      value={questionnaireData.climate_emissions.reporting_year?.toString()}
                      onValueChange={(value) => updateSection("climate_emissions", "reporting_year", parseInt(value))}
                    >
                      <SelectTrigger data-testid="select-reporting-year">
                        <SelectValue placeholder="Select year" />
                      </SelectTrigger>
                      <SelectContent>
                        {[2024, 2023, 2022, 2021, 2020].map(year => (
                          <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-3">
                  <Label>What is your Science-Based Target (SBTi) status? *</Label>
                  <RadioGroup
                    value={questionnaireData.climate_emissions.sbti_status}
                    onValueChange={(value) => updateSection("climate_emissions", "sbti_status", value)}
                    data-testid="radio-sbti"
                  >
                    {["Not assessed", "No target", "Committed", "Validated", "Net-Zero Target Approved"].map(option => (
                      <div key={option} className="flex items-center space-x-2">
                        <RadioGroupItem value={option} id={`sbti-${option}`} />
                        <Label htmlFor={`sbti-${option}`} className="font-normal">{option}</Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>

                {questionnaireData.climate_emissions.sbti_status && 
                 questionnaireData.climate_emissions.sbti_status !== "No target" &&
                 questionnaireData.climate_emissions.sbti_status !== "Not assessed" && (
                  <div className="space-y-2">
                    <Label htmlFor="target_year">Target Year</Label>
                    <Select
                      value={questionnaireData.climate_emissions.target_year?.toString()}
                      onValueChange={(value) => updateSection("climate_emissions", "target_year", parseInt(value))}
                    >
                      <SelectTrigger data-testid="select-target-year">
                        <SelectValue placeholder="Select target year" />
                      </SelectTrigger>
                      <SelectContent>
                        {[2025, 2030, 2035, 2040, 2045, 2050].map(year => (
                          <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </div>
            )}

            {/* Section 3: Water Management */}
            {currentSection === 3 && (
              <div className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="water">Total Annual Water Withdrawal (m³) *</Label>
                  <Input
                    id="water"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="e.g., 50000.00"
                    value={questionnaireData.water_management.total_water_withdrawal}
                    onChange={(e) => updateSection("water_management", "total_water_withdrawal", e.target.value)}
                    data-testid="input-water"
                  />
                </div>

                <div className="space-y-3">
                  <Label>Water Stress Level (Primary Operating Region) *</Label>
                  <RadioGroup
                    value={questionnaireData.water_management.water_stress_level}
                    onValueChange={(value) => updateSection("water_management", "water_stress_level", value)}
                    data-testid="radio-water-stress"
                  >
                    {["Low", "Low to Medium", "Medium", "High", "Extremely High"].map(option => (
                      <div key={option} className="flex items-center space-x-2">
                        <RadioGroupItem value={option} id={`water-${option}`} />
                        <Label htmlFor={`water-${option}`} className="font-normal">{option}</Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>

                <div className="space-y-3">
                  <Label>Do you have a formal water management or mitigation plan?</Label>
                  <RadioGroup
                    value={questionnaireData.water_management.water_mitigation_plan}
                    onValueChange={(value) => updateSection("water_management", "water_mitigation_plan", value)}
                    data-testid="radio-water-plan"
                  >
                    {["Yes - Documented", "Yes - Informal practices", "No"].map(option => (
                      <div key={option} className="flex items-center space-x-2">
                        <RadioGroupItem value={option} id={`plan-${option}`} />
                        <Label htmlFor={`plan-${option}`} className="font-normal">{option}</Label>
                      </div>
                    ))}
                  </RadioGroup>
                  {questionnaireData.water_management.water_mitigation_plan?.includes("Yes") && (
                    <Textarea
                      placeholder="Describe key actions"
                      value={questionnaireData.water_management.water_plan_description}
                      onChange={(e) => updateSection("water_management", "water_plan_description", e.target.value)}
                      className="mt-2"
                      data-testid="textarea-water-plan"
                    />
                  )}
                </div>
              </div>
            )}

            {/* Section 4: Waste & Circularity */}
            {currentSection === 4 && (
              <div className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="waste_rate">Waste Diversion Rate (%) *</Label>
                  <Input
                    id="waste_rate"
                    type="number"
                    min="0"
                    max="100"
                    step="0.01"
                    placeholder="e.g., 75.5"
                    value={questionnaireData.waste_circularity.waste_diversion_rate}
                    onChange={(e) => updateSection("waste_circularity", "waste_diversion_rate", e.target.value)}
                    data-testid="input-waste-rate"
                  />
                  <p className="text-xs text-muted-foreground">Percentage of total waste diverted from landfill (0-100)</p>
                </div>

                <div className="space-y-3">
                  <Label>Have you implemented circular economy initiatives?</Label>
                  <RadioGroup
                    value={questionnaireData.waste_circularity.circular_innovation}
                    onValueChange={(value) => updateSection("waste_circularity", "circular_innovation", value)}
                    data-testid="radio-circular"
                  >
                    {[
                      "Yes - Product redesign",
                      "Yes - Material substitution",
                      "Yes - Process optimization",
                      "Yes - Packaging innovation",
                      "No",
                      "In development"
                    ].map(option => (
                      <div key={option} className="flex items-center space-x-2">
                        <RadioGroupItem value={option} id={`circular-${option}`} />
                        <Label htmlFor={`circular-${option}`} className="font-normal">{option}</Label>
                      </div>
                    ))}
                  </RadioGroup>
                  {questionnaireData.waste_circularity.circular_innovation?.includes("Yes") && (
                    <Textarea
                      placeholder="Describe initiative"
                      value={questionnaireData.waste_circularity.circular_description}
                      onChange={(e) => updateSection("waste_circularity", "circular_description", e.target.value)}
                      className="mt-2"
                      data-testid="textarea-circular"
                    />
                  )}
                </div>
              </div>
            )}

            {/* Section 5: Social Responsibility */}
            {currentSection === 5 && (
              <div className="space-y-6">
                <div className="grid md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="employees">Total Employees *</Label>
                    <Input
                      id="employees"
                      type="number"
                      min="0"
                      placeholder="e.g., 500"
                      value={questionnaireData.social_responsibility.total_employees}
                      onChange={(e) => updateSection("social_responsibility", "total_employees", e.target.value)}
                      data-testid="input-employees"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="permanent">Permanent Employees (%)</Label>
                    <Input
                      id="permanent"
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      placeholder="e.g., 85.0"
                      value={questionnaireData.social_responsibility.permanent_employee_percent}
                      onChange={(e) => updateSection("social_responsibility", "permanent_employee_percent", e.target.value)}
                      data-testid="input-permanent"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="women">Women in Management (%)</Label>
                    <Input
                      id="women"
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      placeholder="e.g., 35.0"
                      value={questionnaireData.social_responsibility.women_management_percent}
                      onChange={(e) => updateSection("social_responsibility", "women_management_percent", e.target.value)}
                      data-testid="input-women-mgmt"
                    />
                  </div>
                </div>

                <div className="space-y-3">
                  <Label>Do you have a formal diversity & inclusion policy?</Label>
                  <RadioGroup
                    value={questionnaireData.social_responsibility.diversity_policy}
                    onValueChange={(value) => updateSection("social_responsibility", "diversity_policy", value)}
                    data-testid="radio-diversity"
                  >
                    {["Yes - Public", "Yes - Internal", "In development", "No"].map(option => (
                      <div key={option} className="flex items-center space-x-2">
                        <RadioGroupItem value={option} id={`diversity-${option}`} />
                        <Label htmlFor={`diversity-${option}`} className="font-normal">{option}</Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-3">
                    <Label>Child Labor Policy?</Label>
                    <RadioGroup
                      value={questionnaireData.social_responsibility.child_labor_policy === true ? "Yes" : questionnaireData.social_responsibility.child_labor_policy === false ? "No" : ""}
                      onValueChange={(value) => updateSection("social_responsibility", "child_labor_policy", value === "Yes")}
                      data-testid="radio-child-labor"
                    >
                      {["Yes", "No"].map(option => (
                        <div key={option} className="flex items-center space-x-2">
                          <RadioGroupItem value={option} id={`child-${option}`} />
                          <Label htmlFor={`child-${option}`} className="font-normal">{option}</Label>
                        </div>
                      ))}
                    </RadioGroup>
                  </div>
                  <div className="space-y-3">
                    <Label>Forced Labor Policy?</Label>
                    <RadioGroup
                      value={questionnaireData.social_responsibility.forced_labor_policy === true ? "Yes" : questionnaireData.social_responsibility.forced_labor_policy === false ? "No" : ""}
                      onValueChange={(value) => updateSection("social_responsibility", "forced_labor_policy", value === "Yes")}
                      data-testid="radio-forced-labor"
                    >
                      {["Yes", "No"].map(option => (
                        <div key={option} className="flex items-center space-x-2">
                          <RadioGroupItem value={option} id={`forced-${option}`} />
                          <Label htmlFor={`forced-${option}`} className="font-normal">{option}</Label>
                        </div>
                      ))}
                    </RadioGroup>
                  </div>
                </div>

                <div className="space-y-3">
                  <Label>Health & Safety Management System</Label>
                  <RadioGroup
                    value={questionnaireData.social_responsibility.health_safety_system}
                    onValueChange={(value) => updateSection("social_responsibility", "health_safety_system", value)}
                    data-testid="radio-health-safety"
                  >
                    {["ISO 45001 certified", "Internal system", "None"].map(option => (
                      <div key={option} className="flex items-center space-x-2">
                        <RadioGroupItem value={option} id={`safety-${option}`} />
                        <Label htmlFor={`safety-${option}`} className="font-normal">{option}</Label>
                      </div>
                    ))}
                  </RadioGroup>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="ltifr">Lost Time Injury Frequency Rate (LTIFR)</Label>
                    <Input
                      id="ltifr"
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="Per million hours worked"
                      value={questionnaireData.social_responsibility.ltifr}
                      onChange={(e) => updateSection("social_responsibility", "ltifr", e.target.value)}
                      data-testid="input-ltifr"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="safety_training">Safety Training Coverage (%)</Label>
                    <Input
                      id="safety_training"
                      type="number"
                      min="0"
                      max="100"
                      step="0.1"
                      placeholder="e.g., 95.0"
                      value={questionnaireData.social_responsibility.safety_training_percent}
                      onChange={(e) => updateSection("social_responsibility", "safety_training_percent", e.target.value)}
                      data-testid="input-safety-training"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Section 6: Advanced Metrics */}
            {currentSection === 6 && (
              <div className="space-y-6">
                <div className="bg-sage/10 p-4 rounded-lg border border-sage/20 mb-6">
                  <p className="text-sm text-foreground">
                    <strong>Optional Section:</strong> These advanced metrics enable intensity-based calculations 
                    (e.g., tCO₂e per revenue, water per unit production) for enhanced ESG scoring and benchmarking.
                  </p>
                </div>

                <div className="grid md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="revenue">Annual Revenue (USD)</Label>
                    <Input
                      id="revenue"
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="e.g., 10000000"
                      value={questionnaireData.advanced_metrics.revenue_usd}
                      onChange={(e) => updateSection("advanced_metrics", "revenue_usd", e.target.value)}
                      data-testid="input-revenue"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="production">Production Volume</Label>
                    <Input
                      id="production"
                      type="number"
                      min="0"
                      step="0.01"
                      placeholder="Units produced"
                      value={questionnaireData.advanced_metrics.production_volume}
                      onChange={(e) => updateSection("advanced_metrics", "production_volume", e.target.value)}
                      data-testid="input-production"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="emp_count">Total Employees</Label>
                    <Input
                      id="emp_count"
                      type="number"
                      min="0"
                      placeholder="e.g., 500"
                      value={questionnaireData.advanced_metrics.employees_count}
                      onChange={(e) => updateSection("advanced_metrics", "employees_count", e.target.value)}
                      data-testid="input-emp-count"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between pt-6 border-t border-border">
              <Button
                variant="outline"
                onClick={handlePrevious}
                disabled={currentSection === 0}
                className="btn-outline"
                data-testid="btn-previous"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Previous
              </Button>
              
              {currentSection < sections.length - 1 ? (
                <Button
                  onClick={handleNext}
                  className="btn-primary"
                  data-testid="btn-next"
                >
                  Save & Continue
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button
                  onClick={handleSubmit}
                  disabled={isSubmitting}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-full px-8"
                  data-testid="btn-submit"
                >
                  {isSubmitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Submitting...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-4 h-4 mr-2" />
                      Submit Questionnaire
                    </>
                  )}
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default ESGQuestionnaire;
